import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import clsx from "clsx";
import { Dropdown } from "react-bootstrap";
import objectPath from "object-path";
import { useHtmlClassService } from "../../../../_core/MetronicLayout";
import { SearchResult } from "./SearchResult";
import { DropdownTopbarItemToggler } from "../../../../../_partials/dropdowns";
import axios from "axios";
import { useIntl } from "react-intl";
import NoRecord from "../../../../../_partials/widgets/NoRecord";

// Below this a search is not worth running: it matches most of the database and
// every keystroke costs a full scan of eight tables.
const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 500;

/**
 * The global search box in the topbar.
 *
 * @returns {JSX.Element}
 */
export function SearchDropdown() {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  // Dropdown visibility is controlled, so it has to be state: deriving it from
  // the term alone means Escape and outside clicks cannot close the menu while
  // what is typed is still searchable.
  const [isOpen, setIsOpen] = useState(false);
  // Holds the in-flight request so a slower earlier one cannot land after a
  // newer one and overwrite the results with the wrong answer.
  const inFlight = useRef(null);
  // Aborting cannot stop a response that has already arrived: its continuation
  // still runs and would write "ab" results over a search for "abc". Every
  // request carries the generation it was issued for, and only the current
  // generation may touch state.
  const generation = useRef(0);

  const term = searchValue.trim();
  const isSearchable = term.length >= MIN_SEARCH_LENGTH;

  /**
   * Fetch results for the term currently in the box.
   *
   * @param {AbortSignal} signal Aborts the request when the term changes again.
   * @param {number} issuedFor The generation this call belongs to; a response
   *   for an older one is discarded rather than rendered.
   * @returns {Promise<void>}
   */
  const handleSearch = useCallback(async (signal, issuedFor) => {
    setFailed(false);
    try {
      const response = await axios.get(
        import.meta.env.REACT_APP_SITE_URL +
          `global-search-union?global_search=${encodeURIComponent(term)}`,
        { signal }
      );
      if (signal.aborted || issuedFor !== generation.current) {
        return;
      }

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setData([]);
      }
      setLoading(false);
    } catch (error) {
      // A cancelled request is expected - the user simply kept typing.
      if (axios.isCancel(error) || error.name === "CanceledError") {
        return;
      }

      if (issuedFor !== generation.current) {
        return;
      }

      setFailed(true);
      setLoading(false);
    }
  }, [term]);

  /**
   * Take a new search term from the input and reset what is on screen for it.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The input change.
   * @returns {void}
   */
  const handleSearchChange = (event) => {
    const value = event.target.value;
    generation.current += 1;
    setData(null);
    setFailed(false);
    setSearchValue(value);
    // Show the spinner straight away rather than "no record found" during the
    // debounce, which read as a result when it was not one.
    setLoading(value.trim().length >= MIN_SEARCH_LENGTH);
    setIsOpen(value.trim().length >= MIN_SEARCH_LENGTH);
  };

  const uiService = useHtmlClassService();
  const layoutProps = useMemo(() => {
    return {
      offcanvas:
        objectPath.get(uiService.config, "extras.search.layout") ===
        "offcanvas",
    };
  }, [uiService]);

  useEffect(() => {
    if (!isSearchable) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const delayDebounceFn = setTimeout(() => {
      if (inFlight.current) {
        inFlight.current.abort();
      }
      inFlight.current = controller;
      handleSearch(controller.signal, generation.current);
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(delayDebounceFn);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, isSearchable]);

  return (
    <>
      {!layoutProps.offcanvas && (
        <Dropdown
          alignRight
          drop="down"
          show={isOpen && isSearchable}
          onToggle={(nextShow) => {
            setIsOpen(nextShow);

            if (!nextShow) {
              setData(null);
              setLoading(false);
              setFailed(false);
            }
          }}
          id="kt_quick_search_toggle"
        >
          <Dropdown.Toggle as={DropdownTopbarItemToggler}>
            <div style={{ position: "relative" }}>
              <input
                style={{ paddingRight: "40px", marginBottom: "1px" }}
                type="text"
                placeholder={intl.formatMessage({ id: "Search..." })}
                value={searchValue}
                onChange={handleSearchChange}
                className="form-control border50"
              />
              <i
                style={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  right: "15px",
                }}
                className="fa fa-search"
                aria-hidden="true"
              ></i>
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="dropdown-menu p-0 m-0 dropdown-menu-right dropdown-menu-anim-up dropdown-menu-lg w-100"
          >
            <div
              id="kt_quick_search_dropdown"
              className={clsx("quick-search quick-search-dropdown", {
                "quick-search-has-result": data && data.length,
              })}
            >
              {loading ? (
                <div className="btn btn-icon btn-clean btn-lg btn-dropdown mr-1 w-100">
                  <span
                    className="input-group-append spinner spinner-sm spinner-primary"
                    style={{ padding: "8px" }}
                  />
                </div>
              ) : failed ? (
                <div className="text-muted p-4">
                  {intl.formatMessage({
                    id: "Search.Unavailable",
                    defaultMessage: "Search is unavailable, please try again",
                  })}
                </div>
              ) : data && data.length ? (
                <SearchResult data={data} setSearchValue={setSearchValue} />
              ) : data ? (
                <NoRecord />
              ) : null}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      )}
    </>
  );
}
